import React, {useContext, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Loading from "../../components/Common/Loading";
import {SnackBarContext} from "../../snackBarProvider";
import {
    useAwardClashLeagueTOHMutation,
    useGetAroundClashCampaignsTOHQuery,
    useGetClashLeagueRecalculationStatusTOHQuery,
    useGetClashLeagueTopTOHQuery,
    useGetClashLeaguesTOHQuery,
    useGetClashProgressTOHQuery,
    useScheduleClashLeagueRecalculationTOHMutation,
    useSetClashLeagueScoresTOHMutation,
} from "../../core/api/toh-clans-api";
import {
    CLEAR_ACTIONS,
    ClearProgressDialog,
    ClanLeagueProgressCard,
    eventStatus,
    flattenAroundEvents,
    formatDateTime,
    LeagueOverviewCard,
    LeagueRecalculationCard,
} from "../BossEvents/toh";

const CLASH_SCORE_FIELDS = [
    {key: 'points', label: 'Points', requestKey: 'points'},
    {key: 'wins', label: 'Wins', requestKey: 'wins'},
];

const CLASH_RECALC_INVALIDATE_TAGS = [
    'TohClashLeagues',
    'TohClashLeagueTop',
    'TohClashProgress',
];

function ClashCampaignsCard({
    events,
    isLoading,
    isFetching,
    isError,
    error,
    onRefresh,
    onSelectEvent,
    selectedEventId,
    defaultClanId,
}) {
    const {addAlert} = useContext(SnackBarContext);
    const [clearTarget, setClearTarget] = useState(null);

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            addAlert({text: 'Copied to clipboard', type: 'success'});
        } catch {
            addAlert({text: 'Failed to copy', type: 'error'});
        }
    };

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Typography variant="h6">Clash Campaigns</Typography>
                    <Tooltip title="Reload campaigns">
                        <span>
                            <IconButton size="small" onClick={onRefresh} disabled={isFetching}>
                                <RefreshIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                <Loading isLoading={isLoading} isError={isError} error={error}>
                    {events.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Campaign ID</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Leagues</TableCell>
                                    <TableCell align="right"></TableCell>
                                    <TableCell align="center">Clash Progress</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event) => {
                                    const status = eventStatus(event);
                                    const isSelected = selectedEventId === event.id;
                                    return (
                                        <TableRow
                                            key={event.id}
                                            hover
                                            selected={isSelected}
                                            sx={{cursor: 'pointer'}}
                                            onClick={() => onSelectEvent(event.id)}
                                        >
                                            <TableCell>
                                                <Chip
                                                    icon={status.label === 'Active' ? <CheckCircleIcon/> : <ScheduleIcon/>}
                                                    label={status.label}
                                                    color={status.color}
                                                    size="small"
                                                    variant={status.label === 'Active' ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell>{event.id}</TableCell>
                                            <TableCell>{formatDateTime(event.distribution?.start)}</TableCell>
                                            <TableCell>{formatDateTime(event.distribution?.end)}</TableCell>
                                            <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={event.leagueReady ? 'Ready' : 'Not ready'}
                                                    color={event.leagueReady ? 'success' : 'warning'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} sx={{justifyContent: 'flex-end'}}>
                                                    <Tooltip title="Copy campaign ID">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCopy(event.id);
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Button
                                                        size="small"
                                                        variant={isSelected ? 'contained' : 'outlined'}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onSelectEvent(event.id);
                                                        }}
                                                    >
                                                        {isSelected ? 'Selected' : 'Select'}
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} sx={{justifyContent: 'center'}}>
                                                    <Tooltip title={CLEAR_ACTIONS.clash.tooltip}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setClearTarget({type: 'clash', eventId: event.id});
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title={CLEAR_ACTIONS.clashAll.tooltip}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setClearTarget({type: 'clashAll', eventId: event.id});
                                                            }}
                                                        >
                                                            Clear All
                                                        </Button>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography sx={{color: 'text.secondary', py: 2}}>No campaigns</Typography>
                    )}
                </Loading>
            </CardContent>

            <ClearProgressDialog
                target={clearTarget}
                defaultClanId={defaultClanId}
                onClose={() => setClearTarget(null)}
            />
        </Card>
    );
}

function ClashEventsTOH({defaultClanId = ''}) {
    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetAroundClashCampaignsTOHQuery();

    const events = flattenAroundEvents(data);
    const [selectedEventId, setSelectedEventId] = useState('');

    useEffect(() => {
        if (!selectedEventId && events.length > 0) {
            const active = events.find((event) => eventStatus(event).label === 'Active');
            setSelectedEventId((active || events[0]).id);
        }
    }, [events, selectedEventId]);

    return (
        <Box>
            <Box sx={{mb: 2}}>
                <ClashCampaignsCard
                    events={events}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    isError={isError}
                    error={error}
                    onRefresh={refetch}
                    onSelectEvent={setSelectedEventId}
                    selectedEventId={selectedEventId}
                    defaultClanId={defaultClanId}
                />
            </Box>

            <Box sx={{mb: 2}}>
                <ClanLeagueProgressCard
                    eventId={selectedEventId}
                    defaultClanId={defaultClanId}
                    pointsLabel="Points"
                    useProgressQuery={useGetClashProgressTOHQuery}
                    extraScoreFields={[{field: 'wins', label: 'Wins'}]}
                />
            </Box>

            <Box sx={{mb: 2}}>
                <LeagueOverviewCard
                    eventId={selectedEventId}
                    pointsLabel="Points"
                    useLeaguesQuery={useGetClashLeaguesTOHQuery}
                    useLeagueTopQuery={useGetClashLeagueTopTOHQuery}
                    useAwardMutation={useAwardClashLeagueTOHMutation}
                    useSetScoresMutation={useSetClashLeagueScoresTOHMutation}
                    scoreFields={CLASH_SCORE_FIELDS}
                />
            </Box>

            <LeagueRecalculationCard
                events={events}
                defaultTargetEventId={selectedEventId}
                useStatusQuery={useGetClashLeagueRecalculationStatusTOHQuery}
                useScheduleMutation={useScheduleClashLeagueRecalculationTOHMutation}
                invalidateTags={CLASH_RECALC_INVALIDATE_TAGS}
            />
        </Box>
    );
}

export default ClashEventsTOH;
